import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, Inject, OnInit, Renderer2, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ActivateAccount } from '@app/core/models';
import { AccountService } from '@app/core/services/account.service';
import { ConfigService } from '@app/core/services/config.service';
import { NotificationsService } from '@app/core/services/notifications.service';
import { PasswordBaseComponent } from '@app/shared/components/forms/password-base.component';
import { NotificationTypes } from '@core/models/notification/notification-types';
@Component({
  templateUrl: './activate-account.component.html'
})
export class ActivateAccountComponent extends PasswordBaseComponent implements OnInit {

  isProcessing = false;
  id = '';
  passwordRequired = true;
  activeUser = false;

  siteKey: string;

  public recentError?: { error: any };

  @ViewChild('submitB') submitB: ElementRef<HTMLInputElement>;

  constructor(private renderer2: Renderer2, @Inject(DOCUMENT) private document: Document,
    private activatedRoute: ActivatedRoute,
    private notificationsService: NotificationsService,
    private accountService: AccountService,
    private router: Router,
    protected formBuilder: FormBuilder,
    private configService: ConfigService) {

    super(formBuilder);
    this.siteKey = this.configService.appConfig.recaptcha.siteKey;
    let script = renderer2.createElement('script');
    script.src = 'https://www.google.com/recaptcha/enterprise.js';
    script.async = true;
    script.defer = true;
    this.renderer2.appendChild(this.document.head, script);
    window['recaptcha_callback'] = this.recaptchaDataCallback.bind(this);
  }

  ngOnInit(): void {
    this.id = this.activatedRoute.snapshot.queryParams.id;

    this.accountService
      .isPasswordRequired(this.id)
      .then((response) => {
        this.passwordRequired = response.passwordRequired;
        this.activeUser = response.activeUser;
      })
      .finally(() => {
        this.isProcessing = false;
        if (this.activeUser) {
          this.router.navigate(['/']).then(() =>
            this.notificationsService.notify({
              target: 'login',
              text: 'Your account is already active. Please enter your login credentials.',
              headline: '',
              type: NotificationTypes.SUCCESS,
              dismissible: true,
              dismissAfter: 3000
            })
          );
        }
        // This code is required to bypass the validation when passwordRequired=false
        if (!this.passwordRequired) {
          this.form.get('password').disable();
          this.form.get('password').updateValueAndValidity();
          this.form.get('confirmPassword').disable();
          this.form.get('confirmPassword').updateValueAndValidity();
        }
      });
    super.buildForm();
    this.form.addControl('recaptchaToken', new FormControl('', Validators.required));
  }

  onSubmit(): void {

    const activateAccountData = {
      newPassword: this.form.value.password,
      token: this.form.value.pin,
      id: this.id,
      recaptchaEvent: { token: this.form.controls.recaptchaToken.value, siteKey: this.siteKey, expectedAction: "activateAccount" }
    } as ActivateAccount;

    this.isProcessing = true;
    this.notificationsService.clearAll();

    this.accountService
      .activateAccount(activateAccountData)
      .then(() => {
        this.router.navigate(['/']).then(() =>
          this.notificationsService.notify({
            target: 'login',
            text: 'Your account has been successfully activated. If you have access to ' +
              'Labcorp | Link&trade;, the same login credentials can be used for both systems.',
            headline: '',
            type: NotificationTypes.SUCCESS,
            dismissible: true,
            dismissAfter: 3000
          })
        );
      })
      .catch((err: HttpErrorResponse) => {
        if (err.statusText === 'INVALID_REQUEST') {
          this.notificationsService.notify({
            target: 'activate',
            text: 'We were not able to activate your account at this time.',
            headline: '',
            type: NotificationTypes.DANGER,
            dismissible: false,
            dismissAfter: 10000
          });
        } else if (err.statusText === 'TOKEN_EXPIRED') {
          this.notificationsService.notify({
            target: 'activate',
            text: 'Your PIN has expired, please contact your customer administrator or Managed Care Executive to re-send your activation.',
            headline: '',
            type: NotificationTypes.DANGER,
            dismissible: false,
            dismissAfter: 10000
          });
        }
      })
      .finally(() => {
        this.isProcessing = false;
      });
  }

  onCancel(): void {
    this.router.navigate(['/']);
  }

  recaptchaDataCallback(token): void {
    this.form.controls.recaptchaToken.setValue(token);
    if (this.form.valid) {
      this.submitB.nativeElement.disabled = false;
    }
  }
}
